import { useMemo, useRef } from 'react';
import PortfolioPicture from './PortfolioPicture';
import Lightbox from './Lightbox';
import useLightbox from '../hooks/useLightbox';
import { IconExpand } from './ui/Icon';
import {
    GALLERY_LABELS,
    getPortfolioCaption,
    resolveGalleryLocale
} from '../data/portfolio-captions';
import './portfolio.css';

// The archive sheet: the feature over two rows beside the title, two plates
// under the title, then strips of four (the last strip may run short).
const STRIP = 4;
const COLS_SMALL = 3;
const COLS_FEATURE = 6;

// `sizes` = rendered plate width (plates keep the photos' native aspect, so the
// slot width is the image width). Measured from the grids in portfolio.css:
// "archive" is a 12-column sheet from 1024px (feature 6, plates 3), "column"
// a two-column sheet in the service pages' 7/12 column; below 1024px both
// are two columns with the feature full width. Re-measure if the grids change.
const SIZES = {
    archive: {
        feature: '(min-width: 1024px) min(45vw, 640px), calc(100vw - 2.5rem)',
        small: '(min-width: 1024px) min(21vw, 300px), 45vw'
    },
    column: {
        feature: '(min-width: 1024px) min(54vw, 760px), calc(100vw - 2.5rem)',
        small: '(min-width: 1024px) min(26vw, 370px), 45vw'
    }
};

/**
 * Where each plate sits on the 12-column archive sheet (desktop), as CSS
 * custom properties; the title cell (columns 7–12, row 1) stays clear.
 *
 * @param {number} index
 * @param {number} count
 * @returns {{ style: Record<string, number>, rows: number }}
 */
function archiveCell(index, count) {
    const rows = 2 + Math.max(0, Math.ceil((count - 3) / STRIP));
    if (index === 0) return { rows, style: { '--c': 1, '--cs': COLS_FEATURE, '--r': 1, '--rs': 2 } };
    if (index <= 2) return { rows, style: { '--c': 7 + (index - 1) * COLS_SMALL, '--cs': COLS_SMALL, '--r': 2, '--rs': 1 } };
    const k = index - 3;
    return { rows, style: { '--c': 1 + (k % STRIP) * COLS_SMALL, '--cs': COLS_SMALL, '--r': 3 + Math.floor(k / STRIP), '--rs': 1 } };
}

/**
 * Photographs of parts as cut film plates, with their own lightbox. Two
 * layouts: "archive" (the home gallery: a 12-column sheet whose title sits
 * in the sheet's top-right cell, beside the feature plate) and "column"
 * (a service page's examples: two columns, the feature full width).
 * `images` are file names in /public.
 *
 * @param {{
 *   images: string[],
 *   lang?: 'tr' | 'en' | 'de',
 *   label: string,
 *   labelledBy?: string,
 *   layout?: 'archive' | 'column',
 *   head?: import('react').ReactNode,
 * }} props
 */
export default function PlateSheet({ images, lang = 'tr', label, labelledBy, layout = 'archive', head = null }) {
    const files = useMemo(() => (Array.isArray(images) ? images : []), [images]);
    const locale = resolveGalleryLocale(lang);
    const labels = GALLERY_LABELS[locale];
    const captions = useMemo(
        () => files.map((file, i) => getPortfolioCaption(file, locale, i)),
        [files, locale]
    );

    const lightbox = useLightbox(files);
    const openerRef = useRef(null);

    if (files.length === 0) return null;

    const rows = archiveCell(0, files.length).rows;

    const openAt = (event, index) => {
        openerRef.current = event.currentTarget;
        lightbox.open(index);
    };

    return (
        <div className={`archive__layout archive__layout--${layout}`} style={{ '--rows': rows }}>
            {head ? <div className="archive__head">{head}</div> : null}

            <ul className="archive__sheet" role="list" aria-labelledby={labelledBy}>
                {files.map((file, index) => {
                    const size = index === 0 ? 'feature' : 'small';
                    const caption = captions[index];

                    return (
                        <li
                            key={`${file}-${index}`}
                            className={`archive__item archive__item--${size}`}
                            style={layout === 'archive' ? archiveCell(index, files.length).style : undefined}
                            data-reveal="rise"
                        >
                            <figure className="archive__figure">
                                <button
                                    type="button"
                                    className="archive__plate plate plate--marks"
                                    aria-label={`${caption} — ${labels.enlarge}`}
                                    onClick={(event) => openAt(event, index)}
                                >
                                    <PortfolioPicture file={file} alt={caption} sizes={SIZES[layout][size]} />
                                    <span className="archive__expand" aria-hidden="true">
                                        <IconExpand size={18} />
                                    </span>
                                </button>
                                <figcaption className="archive__caption">
                                    <span className="legend archive__name">{caption}</span>
                                </figcaption>
                            </figure>
                        </li>
                    );
                })}
            </ul>

            <Lightbox
                isOpen={lightbox.isOpen}
                images={files}
                index={lightbox.index}
                direction={lightbox.direction}
                captions={captions}
                title={label}
                labels={labels}
                onClose={lightbox.close}
                onPrev={lightbox.prev}
                onNext={lightbox.next}
                touchHandlers={lightbox.touchHandlers}
                returnFocusRef={openerRef}
            />
        </div>
    );
}
